/**
 * TourButton
 * A small button that launches the guided tour for the current page context.
 * Appears as a subtle icon button that users can click to start the tour.
 */

import { useTourGuide } from './TourGuide';
import { type TourDefinition } from './tourSteps';
import './TourGuide.css';

interface TourButtonProps {
  tour: TourDefinition;
  /** Audio source URLs keyed by step index */
  audioSources?: Record<number, string>;
  /** Show a "New" badge if the tour hasn't been seen yet */
  showNewBadge?: boolean;
  /** Variant: 'icon' for compact, 'text' for labelled button */
  variant?: 'icon' | 'text';
}

export function TourButton({
  tour,
  audioSources,
  showNewBadge = true,
  variant = 'icon',
}: Readonly<TourButtonProps>) {
  const { startTour, hasBeenSeen } = useTourGuide({ tour, audioSources });

  if (variant === 'text') {
    return (
      <button
        type="button"
        className="tour-button tour-button--text"
        onClick={startTour}
        aria-label={`Take the guided tour: ${tour.label}`}
      >
        <TourIcon />
        <span>Take the tour</span>
        {showNewBadge && !hasBeenSeen && (
          <span className="tour-badge">New</span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      className="tour-button tour-button--icon"
      onClick={startTour}
      title={`Take the guided tour: ${tour.label}`}
      aria-label={`Take the guided tour: ${tour.label}`}
    >
      <TourIcon />
      {showNewBadge && !hasBeenSeen && (
        <span className="tour-badge-dot" aria-hidden="true" />
      )}
    </button>
  );
}

function TourIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Question mark circle — universal "help/explain" affordance */}
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
